import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

/**
 * Desktop guided-tour provider — mirrors the mobile API so tour
 * definitions remain shared. Persistence is via localStorage here (no
 * Redux-persist on the desktop app).
 */
const GuidedTourContext = createContext(null);

export const useGuidedTour = () => {
  const ctx = useContext(GuidedTourContext);
  if (!ctx) throw new Error("useGuidedTour must be used inside GuidedTourProvider");
  return ctx;
};

const STORAGE_KEY = "tor_tourProgress";
const MUTE_KEY = "tor_tourMuted";
const DEFAULT_PROGRESS = {
  welcomeSeen: false,
  services: { completed: false, currentStep: 0 },
  workingHours: { completed: false, currentStep: 0 },
  calendar: { completed: false, currentStep: 0 },
};

const loadProgress = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PROGRESS;
    return { ...DEFAULT_PROGRESS, ...JSON.parse(raw) };
  } catch (e) {
    return DEFAULT_PROGRESS;
  }
};

const saveProgress = (p) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch (e) {}
};

export const GuidedTourProvider = ({ children }) => {
  const [progress, setProgress] = useState(loadProgress);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [activeTour, setActiveTour] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [muted, setMutedState] = useState(() => {
    try { return localStorage.getItem(MUTE_KEY) === "1"; } catch (e) { return false; }
  });

  const toggleMuted = useCallback(() => {
    setMutedState((m) => {
      const next = !m;
      try { localStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch (e) {}
      return next;
    });
  }, []);

  // Persist progress on change.
  useEffect(() => { saveProgress(progress); }, [progress]);

  const openWelcome = useCallback(() => setWelcomeOpen(true), []);
  const closeWelcome = useCallback(() => setWelcomeOpen(false), []);
  const openList = useCallback(() => setListOpen(true), []);
  const closeList = useCallback(() => setListOpen(false), []);

  const startTour = useCallback((tourId, initialStep = 0) => {
    setWelcomeOpen(false);
    setListOpen(false);
    setActiveTour(tourId);
    setStepIndex(initialStep);
  }, []);

  const endTour = useCallback(() => {
    setActiveTour(null);
    setStepIndex(0);
  }, []);

  const nextStep = useCallback(() => setStepIndex((i) => i + 1), []);
  const prevStep = useCallback(() => setStepIndex((i) => Math.max(0, i - 1)), []);

  const markWelcomeSeen = useCallback(() => {
    setProgress((p) => ({ ...p, welcomeSeen: true }));
  }, []);

  const setTourStepProgress = useCallback((tourId, step) => {
    setProgress((p) => ({
      ...p,
      [tourId]: { ...(p[tourId] || { completed: false, currentStep: 0 }), currentStep: step },
    }));
  }, []);

  const completeTourProgress = useCallback((tourId) => {
    setProgress((p) => ({
      ...p,
      [tourId]: { ...(p[tourId] || { completed: false, currentStep: 0 }), completed: true },
    }));
  }, []);

  const resetTour = useCallback((tourId) => {
    setProgress((p) => ({ ...p, [tourId]: { completed: false, currentStep: 0 } }));
  }, []);

  const value = {
    // state
    progress,
    welcomeOpen,
    listOpen,
    activeTour,
    stepIndex,
    // actions
    openWelcome,
    closeWelcome,
    openList,
    closeList,
    startTour,
    endTour,
    nextStep,
    prevStep,
    markWelcomeSeen,
    setTourStepProgress,
    completeTourProgress,
    resetTour,
    // Audio
    muted,
    toggleMuted,
  };

  return (
    <GuidedTourContext.Provider value={value}>{children}</GuidedTourContext.Provider>
  );
};

export default GuidedTourProvider;
